import React, { useState, useMemo, useEffect } from "react";
import { Form, Formik, useFormikContext } from "formik";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Dialog from "@mui/material/Dialog";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import Divider from "@mui/material/Divider";

import CheckboxField from "../../components/form/CheckboxField";
import useFields from "../../hooks/useFields";
import Order from "../../components/TablePro/Order";
import useAuth from "../../hooks/useAuth";
import { useConfigurableFields } from "../../contexts/configurable-fields";
import ConfigurableFieldDialog from "../../views/settings/ConfigurableFieldDialog";
import EditConfigurableFieldDialog from "../../views/settings/EditConfigurableFieldDialog";
import UnsavedChangesDialog from "../modals/UnsavedChanges/UnsavedChangesDialog";

const CONFIGURABLE_FIELDS_TAB = 99998;
const REORDER_TAB = 99999;
const hiddenScrubberFields = ["barcode"];

const DirtyTracker = ({ onChange }) => {
  const { dirty } = useFormikContext();

  useEffect(() => {
    onChange(dirty);
  }, [dirty, onChange]);

  return null;
};

const ConfigurableCheckboxField = ({ name, category, label, onEdit }) => (
  <CheckboxField 
    name={name} 
    category={category} 
    label={
      <Box
        component="span"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onEdit(name);
        }}
        sx={{
          textDecoration: 'underline',
          cursor: 'pointer',
          '&:hover': {
            color: 'primary.main'
          }
        }}
      >
        {label}
      </Box>
    }
  />
);

export default function FieldsModal({
  type,
  initialValues,
  reorderFields,
  onChangeOrder,
  onToggle,
  onSubmit,
  useProjectSettingsModel = false,
}) {
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selected, setSelected] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [addFieldDialogOpen, setAddFieldDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [showAllConfigurableFields, setShowAllConfigurableFields] = useState(false);
  const [selectedConfigurableOverrides, setSelectedConfigurableOverrides] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedChanges, setShowUnsavedChanges] = useState(false);
  const fields = useFields();
  const { user } = useAuth();
  const { settings } = user.company;
  const { configurableFields, customFields } = useConfigurableFields();
  
  const options = useMemo(() => {
    return fields[selected]?.list
      ?.filter(([name]) => {
        if (type === "scrubber") {
          return !hiddenScrubberFields.includes(name);
        }
        return true;
      })
      .map(([name, ...rest]) => {
        if (type === "scrubber" && !useProjectSettingsModel) {
          if (name === "photo") {
            return ["photo_carousel", ...rest];
          } else if (name === "barcode") {
            return ["barcode_scanner", ...rest];
          } else if (name === "barcode_number") {
            return ["barcode", ...rest];
          } else {
            return [name, ...rest];
          }
        } else {
          return [name, ...rest];
        }
      });
  }, [fields, selected, type, useProjectSettingsModel]);

  // Global search across all categories
  const allFieldsWithCategory = useMemo(() => {
    const allFields = [];
    fields.forEach((fieldGroup, index) => {
      const groupOptions = fieldGroup.list
        ?.filter(([name]) => {
          if (type === "scrubber") {
            return !hiddenScrubberFields.includes(name);
          }
          return true;
        })
        .map(([name, ...rest]) => {
          if (type === "scrubber" && !useProjectSettingsModel) {
            if (name === "photo") {
              return ["photo_carousel", ...rest, index];
            } else if (name === "barcode") {
              return ["barcode_scanner", ...rest, index];
            } else if (name === "barcode_number") {
              return ["barcode", ...rest, index];
            } else {
              return [name, ...rest, index];
            }
          } else {
            return [name, ...rest, index];
          }
        });
      if (groupOptions) {
        allFields.push(...groupOptions);
      }
    });
    return allFields;
  }, [fields, type, useProjectSettingsModel]);

  const searchResults = useMemo(() => {
    if (!searchQuery) return null;
    const query = searchQuery.toLowerCase();
    
    // Search through all fields
    const matchingFields = allFieldsWithCategory.filter(([name, category, label]) => 
      label?.toLowerCase().includes(query) || 
      name?.toLowerCase().includes(query)
    );

    // Search through configurable fields if enabled
    const matchingConfigurable = settings.user_configurable_custom_fields
      ? configurableFields?.filter(([name, category, label]) => 
          label?.toLowerCase().includes(query) || 
          name?.toLowerCase().includes(query)
        ) || []
      : [];

    return {
      standardFields: matchingFields,
      configurableFields: matchingConfigurable,
      total: matchingFields.length + matchingConfigurable.length
    };
  }, [searchQuery, allFieldsWithCategory, configurableFields, settings.user_configurable_custom_fields]);

  const displayOptions = searchQuery && searchResults ? searchResults.standardFields : options;
  const displayConfigurableFields = searchQuery && searchResults ? searchResults.configurableFields : configurableFields;
  const formInitialValues = useMemo(
    () => ({ ...initialValues, ...selectedConfigurableOverrides }),
    [initialValues, selectedConfigurableOverrides]
  );

  const handleSave = values => {
    setSubmitLoading(true);
    return onSubmit(values).then(saved => {
      setSubmitLoading(false);
      return saved;
    });
  };
  const handleRequestClose = () => {
    if (isDirty) {
      setShowUnsavedChanges(true);
      return;
    }
    onToggle();
  };

  const getCustomFieldTemplate = (fieldName) => {
    if (!customFields || !fieldName.startsWith('custom_field_')) return null;
    const fieldId = fieldName.replace('custom_field_', '');
    return customFields.find(template => template.id === fieldId);
  };

  const handleEditField = (fieldName) => {
    const template = getCustomFieldTemplate(fieldName);
    if (template) {
      setEditingField(template);
    }
  };

  return (
    <Dialog 
      open 
      maxWidth={false}
      onClose={handleRequestClose}
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '70vh',
          maxHeight: '85vh',
          width: '650px',
          maxWidth: '90vw',
          '@media (max-width: 500px)': {
            width: '100%',
            maxWidth: '100%',
            maxHeight: '100%',
            borderRadius: 0,
            margin: 0
          }
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          background: "#fff",
          borderBottom: "1px solid #e0e0e0",
          py: 2,
          px: 3
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box width={40} />
          <Typography variant="h4" fontWeight={500} color="text.primary">
            {settings.filter_field2} Fields
          </Typography>
          <IconButton onClick={handleRequestClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0, height: 'calc(70vh - 120px)', display: 'flex', flexDirection: 'column' }}>
        <Formik enableReinitialize initialValues={formInitialValues} onSubmit={handleSave}>
          {({ submitForm, values, setFieldValue }) => (
            <Form style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <DirtyTracker onChange={setIsDirty} />
              {/* Global Search Bar */}
              {selected !== REORDER_TAB && (
                <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #e0e0e0', bgcolor: '#fff', flexShrink: 0 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search fields..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: '#fafafa',
                      }
                    }}
                  />
                </Box>
              )}
              <Box display="flex" flexGrow={1} minHeight={0} overflow="hidden">
                {/* Sidebar */}
                {!searchQuery && (
                  <Box 
                    sx={{ 
                      width: { xs: 120, sm: 200 },
                      flexShrink: 0,
                      borderRight: "1px solid #e0e0e0",
                      bgcolor: '#fafafa',
                      overflow: 'auto'
                    }}
                  >
                  <List component="nav" sx={{ py: 1 }}>
                    {reorderFields && (
                      <ListItemButton
                        selected={selected === REORDER_TAB}
                        onClick={() => {
                          submitForm().then(() => {
                            setSelected(REORDER_TAB);
                            setSearchQuery("");
                          });
                        }}
                        sx={{
                          mx: { xs: 0.5, sm: 1 },
                          px: { xs: 1, sm: 2 },
                          borderRadius: 1,
                          mb: 0.5,
                          '&.Mui-selected': {
                            bgcolor: 'primary.50',
                            color: 'primary.main',
                            fontWeight: 500,
                            '&:hover': {
                              bgcolor: 'primary.100',
                            }
                          }
                        }}
                      >
                        <Typography variant="body1" fontWeight={500} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Reorder Columns</Typography>
                      </ListItemButton>
                    )}
                    {fields.map((v, i) => (
                      <ListItemButton
                        key={i}
                        selected={i === selected}
                        onClick={() => {
                          setSelected(i);
                          setSearchQuery("");
                        }}
                        sx={{
                          mx: { xs: 0.5, sm: 1 },
                          px: { xs: 1, sm: 2 },
                          borderRadius: 1,
                          mb: 0.5,
                          py: { xs: 1, sm: 1.5 },
                          '&.Mui-selected': {
                            bgcolor: 'primary.50',
                            color: 'primary.main',
                            fontWeight: 500,
                            '&:hover': {
                              bgcolor: 'primary.100',
                            }
                          }
                        }}
                      >
                        <Typography variant="body1" fontWeight={500} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>{v.label}</Typography>
                      </ListItemButton>
                    ))}
                    {settings.user_configurable_custom_fields && (
                      <ListItemButton
                        selected={selected === CONFIGURABLE_FIELDS_TAB}
                        onClick={() => {
                          setSelected(CONFIGURABLE_FIELDS_TAB);
                          setSearchQuery("");
                        }}
                        sx={{
                          mx: { xs: 0.5, sm: 1 },
                          px: { xs: 1, sm: 2 },
                          borderRadius: 1,
                          mb: 0.5,
                          py: { xs: 1, sm: 1.5 },
                          '&.Mui-selected': {
                            bgcolor: 'primary.50',
                            color: 'primary.main',
                            fontWeight: 500,
                            '&:hover': {
                              bgcolor: 'primary.100',
                            }
                          }
                        }}
                      >
                        <Typography variant="body1" fontWeight={500} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Configurable</Typography>
                      </ListItemButton>
                    )}
                  </List>
                </Box>
                )}

                {/* Main Content */}
                <Box flexGrow={1} display="flex" flexDirection="column" minHeight={0} overflow="hidden">
                  {/* Fields List */}
                  <Box sx={{ 
                    flexGrow: 1, 
                    minHeight: 0,
                    overflow: 'auto', 
                    p: 2, 
                    bgcolor: '#fff',
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      bgcolor: '#f1f1f1',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      bgcolor: '#888',
                      borderRadius: '4px',
                      '&:hover': {
                        bgcolor: '#555',
                      }
                    }
                  }}>
                    {(() => {
                      const selectedOptions = displayOptions;
                      const selectedConfigurableFields =
                        searchQuery || showAllConfigurableFields
                          ? displayConfigurableFields
                          : displayConfigurableFields?.filter(
                              ([name]) => values[name] || selectedConfigurableOverrides[name]
                            );

                      return (
                        <>
                    {searchQuery && searchResults ? (
                      // Global search results
                      <Box>
                        {searchResults.total > 0 ? (
                          <>
                            <Typography variant="body2" color="text.secondary" mb={2}>
                              Found {searchResults.total} field{searchResults.total !== 1 ? 's' : ''} matching "{searchQuery}"
                            </Typography>
                            {searchResults.standardFields.length > 0 && (
                              <Box mb={2}>
                                {searchResults.standardFields.map(([name, category, label]) => (
                                  <CheckboxField key={name} name={name} category={category} label={label} />
                                ))}
                              </Box>
                            )}
                            {searchResults.configurableFields.length > 0 && (
                              <Box>
                                <Typography variant="subtitle2" color="text.secondary" mb={1} mt={2}>
                                  Configurable Fields
                                </Typography>
                                {searchResults.configurableFields.map(([name, category, label]) => (
                                  <ConfigurableCheckboxField 
                                    key={name} 
                                    name={name} 
                                    category={category} 
                                    label={label}
                                    onEdit={handleEditField}
                                  />
                                ))}
                              </Box>
                            )}
                          </>
                        ) : (
                          <Box textAlign="center" py={4}>
                            <Typography color="text.secondary">
                              No fields found matching "{searchQuery}"
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    ) : selected === REORDER_TAB ? (
                      <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Box p={2}>
                          <Order
                            fields={reorderFields}
                            onChange={onChangeOrder}
                            reorderFields={true}
                          />
                        </Box>
                      </Paper>
                    ) : selected === CONFIGURABLE_FIELDS_TAB && settings.user_configurable_custom_fields ? (
                      <Box>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => setShowAllConfigurableFields(value => !value)}
                          >
                            {showAllConfigurableFields
                              ? "Show selected configurable fields"
                              : "View all configurable fields"}
                          </Button>
                        </Box>
                        {selectedConfigurableFields?.length > 0 ? (
                          selectedConfigurableFields.map(([name, category, label]) => (
                            <ConfigurableCheckboxField 
                              key={name} 
                              name={name} 
                              category={category} 
                              label={label}
                              onEdit={handleEditField}
                            />
                          ))
                        ) : (
                          <Box textAlign="center" py={4}>
                            <Typography color="text.secondary">
                              No fields available
                            </Typography>
                          </Box>
                        )}
                        <Box
                          display="flex"
                          justifyContent="flex-end"
                          mt={0}
                          pb={1}
                          sx={{ position: "sticky", bottom: -16, zIndex: 1, backgroundColor: "#fff" }}
                        >
                          <IconButton
                            aria-label="Add configurable field"
                            onClick={() => setAddFieldDialogOpen(true)}
                            sx={{
                              border: "1px solid",
                              borderColor: "primary.main",
                              color: "primary.main",
                            }}
                          >
                            <AddIcon />
                          </IconButton>
                        </Box>
                      </Box>
                    ) : (
                      <Box>
                        {selectedOptions?.length > 0 ? (
                          selectedOptions.map(([name, category, label]) => (
                            <CheckboxField key={name} name={name} category={category} label={label} />
                          ))
                        ) : (
                          <Box textAlign="center" py={4}>
                            <Typography color="text.secondary">
                              No fields available
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                        </>
                      );
                    })()}
                  </Box>
                </Box>
              </Box>

              {/* Footer - Anchored to Bottom */}
              <Box 
                sx={{ 
                  marginTop: 'auto',
                  borderTop: '1px solid #e0e0e0',
                  px: 3, 
                  py: 2.5, 
                  display: 'flex', 
                  justifyContent: 'flex-end',
                  bgcolor: '#fff',
                  flexShrink: 0
                }}
              >
                {submitLoading && (
                  <Box mr={2} display="flex" alignItems="center">
                    <CircularProgress color="primary" size={22} />
                  </Box>
                )}
                <Button
                  onClick={() => {
                    handleSave(values).then(saved => {
                      if (saved) {
                        onToggle();
                      }
                    });
                  }}
                  type="button"
                  disabled={submitLoading}
                  variant="contained"
                  color="primary"
                  size="large"
                  sx={{ minWidth: 110 }}
                >
                  Save
                </Button>
              </Box>
              <UnsavedChangesDialog
                open={showUnsavedChanges}
                saving={submitLoading}
                onCancel={() => setShowUnsavedChanges(false)}
                onDiscard={() => {
                  setShowUnsavedChanges(false);
                  onToggle();
                }}
                onSave={() => {
                  handleSave(values).then(saved => {
                    if (saved) {
                      setShowUnsavedChanges(false);
                      onToggle();
                    }
                  });
                }}
              />
              {addFieldDialogOpen && (
                <ConfigurableFieldDialog
                  open={addFieldDialogOpen}
                  enabledTemplateIds={Object.entries(values)
                    .filter(([key, enabled]) => key.startsWith("custom_field_") && enabled)
                    .map(([key]) => key.replace("custom_field_", ""))}
                  onClose={() => setAddFieldDialogOpen(false)}
                  onCreated={async (customFieldTemplate, options = {}) => {
                    const fieldKey = `custom_field_${customFieldTemplate.id}`;
                    setSelectedConfigurableOverrides(prev => ({ ...prev, [fieldKey]: true }));
                    await setFieldValue(fieldKey, true, false);
                    setShowAllConfigurableFields(false);
                    const nextValues = { ...values, [fieldKey]: true };
                    if (options.saveImmediately !== false) {
                      await handleSave(nextValues);
                    }
                  }}
                />
              )}
            </Form>
          )}
        </Formik>
      </DialogContent>
      {editingField && (
        <EditConfigurableFieldDialog
          customFieldTemplate={editingField}
          onClose={() => setEditingField(null)}
        />
      )}
    </Dialog>
  );
}
